import cx from 'classnames';
import type { FC, KeyboardEvent, PropsWithChildren, ReactNode, SyntheticEvent } from 'react';

import useTracking from '@zen/utils/hooks/useTracking';
import { createTrackingEvent, createTrackingParentAttribute } from '@zen/utils/tracking';

import Checkbox from '../Checkbox';
import CollapsibleElement from '../CollapsibleElement';

interface Props {
  checked: boolean;
  className?: string;
  disabled?: boolean;
  icon?: ReactNode;
  name?: string;
  onChange: (checked: boolean) => void;
  title: ReactNode;
}

export const displayName: string = 'Design System/CheckboxCard';

const CheckboxCard: FC<PropsWithChildren<Props>> = (props) => {
  const { checked, children, className, disabled = false, icon, name, onChange, title } = props;

  const { trackEvent } = useTracking();

  const classNames: string = cx(
    {
      'border-azure-base': checked && !disabled,
      'border-grey-lighter': !checked || disabled
    },
    'rounded border border-solid bg-white overflow-hidden',
    className
  );

  const headerClassNames: string = cx(
    {
      'border-b border-solid': checked,
      'bg-azure-lightest border-azure-base': checked && !disabled,
      'border-grey-lighter': checked && disabled,
      'bg-grey-lightest cursor-not-allowed': disabled,
      'cursor-pointer': !disabled
    },
    'flex items-center gap-3 px-4 py-2'
  );

  const contentClassNames: string = cx(
    {
      'bg-azure-lightest': checked && !disabled,
      'bg-grey-lightest': checked && disabled
    },
    'p-4'
  );

  const handleClick = (event: SyntheticEvent<HTMLDivElement>): void => {
    if (disabled) {
      return;
    }

    trackEvent(createTrackingEvent(displayName, 'click', event));
    onChange(!checked);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick(event);
    }
  };

  const tabIndex: number = disabled ? -1 : 0;

  return (
    <div
      className={classNames}
      data-component="checkbox-card"
      data-testid="checkbox-card"
      {...createTrackingParentAttribute('checkbox-card')}
    >
      <div
        aria-checked={checked}
        aria-disabled={disabled}
        className={headerClassNames}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        role="checkbox"
        tabIndex={tabIndex}
      >
        <div aria-hidden="true" className="pointer-events-none">
          <Checkbox checked={checked} disabled={disabled} name={name} />
        </div>
        {icon}
        <div className="text-sm text-grey-dark">{title}</div>
      </div>
      <CollapsibleElement isOpened={checked}>
        <div className={contentClassNames}>{children}</div>
      </CollapsibleElement>
    </div>
  );
};

export type { Props as CheckboxCardProps };
export default CheckboxCard;
